import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import {
  TABLE_ROW_HOVER_SX,
  TABLE_STYLES,
} from "../../theme/tablestyles";
import { MEMBERSHIP_TOOLTIP_PROPS } from "./tooltipStyles";

const BRAND_BLUE = "#00336C";

const HEAD_CELL_SX = {
  backgroundColor: BRAND_BLUE,
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const CIRCLE_CHECKBOX_ICONS = {
  icon: <RadioButtonUncheckedIcon />,
  checkedIcon: <RadioButtonCheckedIcon />,
  indeterminateIcon: <RemoveCircleOutlineIcon />,
};

const STATUS_CONFIG = {
  active: { label: "Active", bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
  prospect: { label: "Prospect", bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
  lapsed: { label: "Lapsed", bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
  suspended: { label: "Suspended", bg: "#FCE4EC", color: "#AD1457", border: "#F06292" },
  cancelled: { label: "Cancelled", bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" },
};

const COLUMNS = [
  { id: "name", label: "Organization name", sortable: true, width: 260 },
  { id: "member_type", label: "Member type", sortable: false, width: 150 },
  { id: "membership_tier", label: "Membership tier", sortable: false, width: 160 },
  { id: "status", label: "Status", sortable: true, width: 130 },
  { id: "renewal_date", label: "Renewal date", sortable: false, width: 150 },
  { id: "paid", label: "Paid", sortable: false, width: 190 },
  { id: "engagement_score", label: "Engagement score", sortable: false, width: 160 },
  { id: "actions", label: "Actions", sortable: false, width: 100 },
];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function display(value) {
  return value || "-";
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        height: 24,
        fontSize: 12,
        fontWeight: 700,
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
      }}
    />
  );
}

function EngagementChip({ category }) {
  const normalized = String(category || "unscored").toLowerCase();
  const cfg = {
    high: { label: "High", bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
    moderate: { label: "Moderate", bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
    low: { label: "Low", bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
    unscored: { label: "Unscored", bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" },
  }[normalized] || { label: category || "Unscored", bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" };
  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
      }}
    />
  );
}

export default function MembershipMembersTable({
  members,
  orderBy,
  orderDirection,
  onSort,
  selectedIds,
  allSelected,
  someSelected,
  toggleOne,
  togglePage,
  onOpenProfile,
  onEdit,
  onPaymentToggle,
}) {
  return (
    <TableContainer
      component="div"
      sx={{
        background: BRAND_BLUE,
        overflow: "hidden",
        width: "100%",
        overflowX: "auto",
      }}
    >
      <Table
        size="small"
        sx={{
          minWidth: 1580,
          tableLayout: "fixed",
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="center" padding="checkbox" sx={{ ...HEAD_CELL_SX, width: 48 }}>
              <Checkbox
                indeterminate={someSelected && !allSelected}
                checked={allSelected}
                onChange={togglePage}
                {...CIRCLE_CHECKBOX_ICONS}
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                }}
              />
            </TableCell>
            {COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                align="center"
                sx={{
                  ...HEAD_CELL_SX,
                  width: column.width,
                  minWidth: column.width,
                  maxWidth: column.width,
                }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? orderDirection : "asc"}
                    onClick={() => onSort(column.id)}
                    sx={{
                      color: "#ffffff !important",
                      "&.Mui-active": { color: "#ffffff !important" },
                      "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={{ background: "white" }}>
          {members.map((member, index) => {
            const rowBg = index % 2 === 0 ? "#fafafa" : "white";
            return (
              <TableRow
                key={member.id}
                sx={{
                  ...TABLE_ROW_HOVER_SX,
                  backgroundColor: rowBg,
                  "&:hover": { backgroundColor: "#eef2f7" },
                }}
              >
                <TableCell align="center" padding="checkbox">
                  <Checkbox
                    checked={selectedIds.has(String(member.id))}
                    onChange={() => toggleOne(String(member.id))}
                    {...CIRCLE_CHECKBOX_ICONS}
                    sx={{
                      color: BRAND_BLUE,
                      "&.Mui-checked": { color: BRAND_BLUE },
                    }}
                  />
                </TableCell>
                <TableCell align="left" sx={{ ...TABLE_STYLES.bodyCell, textAlign: "left" }}>
                  <Tooltip title={member.name || ""} {...MEMBERSHIP_TOOLTIP_PROPS}>
                    <Button
                      variant="text"
                      onClick={() => onOpenProfile(member)}
                      sx={{ ...TABLE_STYLES.clickableCell, justifyContent: "flex-start" }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {member.name}
                      </span>
                    </Button>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>{display(member.member_type)}</TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>{display(member.membership_tier)}</TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}><StatusChip status={member.status} /></TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>{formatDate(member.renewal_date)}</TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                  <Stack spacing={0.25} alignItems="center">
                    <Switch
                      size="small"
                      checked={Boolean(member.paid)}
                      onChange={(event) => onPaymentToggle(member, event.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_BLUE },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: BRAND_BLUE,
                        },
                      }}
                    />
                    <Typography sx={{ color: BRAND_BLUE, fontSize: 12, fontWeight: 800 }}>
                      {member.paid ? "Yes" : "No"}
                    </Typography>
                    {member.paid ? (
                      <Typography sx={{ color: "#52677f", fontSize: 11, lineHeight: 1.2 }}>
                        {formatMoney(member.paid_amount)} {member.paid_at ? `on ${formatDate(member.paid_at)}` : ""}
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                  <Stack spacing={0.5} alignItems="center">
                    <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 13 }}>
                      {display(member.engagement_score)}
                    </Typography>
                    <EngagementChip category={member.engagement_category} />
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                    <Tooltip title="Edit member" {...MEMBERSHIP_TOOLTIP_PROPS}>
                      <IconButton size="small" onClick={() => onEdit(member)}>
                        <EditIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
